import * as React from "react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { Table } from "../../newcomponents/table/table";
import { TableNewItemButton } from "../../newcomponents/table/components/tableNewButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { Domain } from "../../schema/types/domain/entity";
import { linksOf } from "../../services/cpln";
import { domainHelpers } from "../../mst/kinds/domain.helpers";
import { TableActionsDropdown } from "../../newcomponents/table/components/tableActionsDropdown";
import { DeleteModal } from "../../components/modals/deleteModal";

interface DomainTableItem extends Domain {
  selfLink: string;
  domainStatusText: string;
  type: string;
  mappedGVC: string;
  endpointCount: number;
}

const DomainListRaw: React.FC = () => {
  const [selectedLinks, setSelectedLinks] = React.useState<string[]>([]);
  const [action, setAction] = React.useState<string>("");

  const { kind, id } = useTableKindId("domain", "domain-list");
  const qData = useTableItemQueryData<Domain>(kind, { fetchAllPages: true });

  const domainTableItems: DomainTableItem[] = qData.visibleItems.map((item) => {
    const selfLink = linksOf(item).self!;
    const domainStatusText = domainHelpers.getDomainStatusText(item);
    const type = domainHelpers.getType(item);
    const mappedGVC = domainHelpers.getMappedGVC(item);
    const endpointCount = domainHelpers.getEndpointCount(item);
    return {
      ...item,
      selfLink: selfLink,
      domainStatusText: domainStatusText,
      type: type,
      mappedGVC: mappedGVC,
      endpointCount: endpointCount,
    };
  });

  return (
    <>
      <Table<DomainTableItem>
        title={"Domains"}
        data={domainTableItems}
        selectKey="selfLink"
        selectMode="multi"
        selections={selectedLinks}
        onSelectionsChange={setSelectedLinks}
        tableId={id}
        headerRenderer={() => {
          return (
            <>
              <TableNewItemButton kind={kind} />
              <TableActionsDropdown
                kind={kind}
                selectionLinks={selectedLinks}
                options={[
                  {
                    key: "delete",
                    label: "Delete",
                    disabled: selectedLinks.length < 1,
                    danger: true,
                    callback: () => setAction("delete"),
                  },
                ]}
              />
              <TableHeaderRefreshButton onClick={qData.fetchItems} />
              <CustomTableCPLNQuery
                tableId={id}
                kind={kind}
                onQuery={qData.setQuery}
                isLoading={qData.isLoadingQueried}
              />
            </>
          );
        }}
        enableFilter
        isLoading={qData.isLoading}
        columns={[
          NameDescriptionColumn(),
          {
            id: "domainStatusText",
            label: "Status",
            canGroupByDistinctValues: true,
            cell: ({ getValue }) => {
              const value = getValue();
              return (
                <span
                  className={clsx("truncate pr-2", {
                    "color-action": value === "Ready",
                    "color-warning": value === "Warning",
                  })}
                >
                  {value}
                </span>
              );
            },
          },
          { id: "type", label: "Type", canGroupByDistinctValues: true },
          { id: "mappedGVC", label: "GVC" },
          { id: "endpointCount", label: "Endpoints" },
          TagsColumn(),
          CreatedColumn(),
          LastModifiedColumn(),
        ]}
      />
      {action === "delete" ? (
        <DeleteModal
          kind={kind}
          onCancel={() => setAction("")}
          onComplete={() => {
            setAction("");
            setSelectedLinks([]);
            qData.fetchItems();
          }}
          links={selectedLinks}
        />
      ) : null}
    </>
  );
};

export const DomainList = observer(DomainListRaw);
